import React, { useState } from 'react';
import { ChevronDown, ChevronRight, HelpCircle } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';

/**
 * Collapsible "How to Use the 1-5 Rating Scale" reference panel.
 *
 * Shown at the top of an evaluation form so both managers (writing the eval)
 * and employees (reviewing before signing) understand what each rating means.
 * Mobile-friendly: collapsed by default on small screens; scale numbers keep
 * a fixed compact column so long descriptions wrap cleanly on phones.
 *
 * Content comes verbatim from Paul Christian's "How to Use the 1-5 Rating
 * Scale" document (Aug 2026) and is editable by Admin/HR via
 * /admin/evaluation-settings.
 *
 * Props:
 *   intro:  string — the framing paragraph (verbatim)
 *   levels: [{ value: number|null, label: string, description: string }, ...]
 *   defaultOpen: boolean — expand on first paint (defaults to open on desktop
 *                only; the CSS-based responsive fallback is handled inside)
 */
export default function RatingScalePanel({ intro, levels, defaultOpen }) {
  // Default to open on ≥md screens, closed on smaller (mobile) — we let user
  // toggle after that. We check window.innerWidth ONCE on mount (SSR-safe).
  const initialOpen = defaultOpen != null
    ? defaultOpen
    : (typeof window !== 'undefined' && window.innerWidth >= 768);
  const [open, setOpen] = useState(initialOpen);

  // Nothing to show if config wasn't loaded yet
  if (!intro && (!levels || levels.length === 0)) return null;

  // Colour ramp per rating value — keeps the panel visually intuitive.
  const swatch = (v) => {
    if (v === 5) return 'bg-emerald-100 text-emerald-800 border-emerald-300';
    if (v === 4) return 'bg-sky-100 text-sky-800 border-sky-300';
    if (v === 3) return 'bg-slate-100 text-slate-800 border-slate-300';
    if (v === 2) return 'bg-amber-100 text-amber-800 border-amber-300';
    if (v === 1) return 'bg-red-100 text-red-800 border-red-300';
    return 'bg-slate-100 text-slate-600 border-slate-300';
  };

  return (
    <Card className="border-blue-200 bg-blue-50/40" data-testid="rating-scale-panel">
      <CardHeader className="py-3 px-4 md:px-6">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          className="flex items-center justify-between w-full text-left focus:outline-none group"
          aria-expanded={open}
          data-testid="rating-scale-toggle"
        >
          <CardTitle className="flex items-center gap-2 text-base md:text-lg text-slate-800">
            <HelpCircle className="w-4 h-4 md:w-5 md:h-5 text-blue-700" />
            How to Use the 1–5 Rating Scale
          </CardTitle>
          {open
            ? <ChevronDown className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />
            : <ChevronRight className="w-5 h-5 text-slate-500 group-hover:text-slate-700" />}
        </button>
      </CardHeader>
      {open && (
        <CardContent className="px-4 md:px-6 pb-5 pt-0">
          {intro && (
            <p className="text-sm text-slate-700 leading-relaxed mb-4">{intro}</p>
          )}
          {levels && levels.length > 0 && (
            <div className="space-y-2">
              {levels.map((lv) => (
                <div
                  key={lv.value == null ? 'na' : lv.value}
                  className="flex gap-3 items-start"
                  data-testid={`scale-level-${lv.value == null ? 'na' : lv.value}`}
                >
                  <span
                    className={`shrink-0 inline-flex items-center justify-center border rounded-md font-bold text-sm md:text-base w-10 h-10 md:w-11 md:h-11 ${swatch(lv.value)}`}
                    aria-label={`Rating ${lv.value == null ? 'N/A' : lv.value}`}
                  >
                    {lv.value == null ? 'N/A' : lv.value}
                  </span>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm md:text-base text-slate-900">{lv.label}</div>
                    <div className="text-xs md:text-sm text-slate-600 leading-relaxed">{lv.description}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
}
